import { useEffect, useState, useCallback } from 'react';
import { readFirm, updateFirmDirectory } from './grcClient.js';

/**
 * FirmDirectoryProfile
 *
 * The firm's own editor for its public directory profile (GACA-05).
 *
 * Listing is an affirmative choice: the firm is invisible to clients until
 * someone here turns it on. Everything on this form is published verbatim to
 * any authenticated Tryggr client, so the copy says so plainly rather than
 * letting a firm discover it later.
 *
 * Nothing about the firm's clients or engagements is published — that is a
 * boundary (GRC-B-011), not a setting, so there is nothing to toggle for it.
 *
 * Firm-admin only; the server enforces the same gate.
 */

const FIELDS = [
  {
    key: 'description',
    label: 'About the firm',
    placeholder: 'What you do, who you typically work with, how you run an audit.',
    multiline: true,
    max: 2000,
  },
  {
    key: 'regions',
    label: 'Regions served',
    placeholder: 'Denmark, Sweden, Norway',
    max: 500,
  },
  {
    key: 'frameworks',
    label: 'Frameworks audited',
    placeholder: 'ISO 27001, NIS2, ISAE 3402',
    max: 500,
  },
  {
    key: 'certifications',
    label: 'Certifications',
    placeholder: 'Accreditations your firm holds',
    max: 500,
  },
  {
    key: 'contactEmail',
    label: 'Public contact email',
    placeholder: 'newbusiness@yourfirm.com',
    max: 200,
    hint: 'Published to clients. Kept separate from your operational contact address, which is never listed.',
  },
  {
    key: 'website',
    label: 'Website',
    placeholder: 'https://yourfirm.com',
    max: 500,
  },
];

const EMPTY = FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: '' }), {});

export default function FirmDirectoryProfile({ session }) {
  const [profile, setProfile] = useState(EMPTY);
  const [listed, setListed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  const isFirmAdmin = session?.role === 'firm-admin';

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await readFirm(session.firmId);
      const d = data.firm?.directory || {};
      setListed(!!d.listed);
      setProfile({
        description: d.description || '',
        regions: d.regions || '',
        frameworks: d.frameworks || '',
        certifications: d.certifications || '',
        contactEmail: d.contactEmail || '',
        website: d.website || '',
      });
    } catch (e) {
      setError(e.message || 'Could not load the directory profile');
    } finally {
      setLoading(false);
    }
  }, [session?.firmId]);

  useEffect(() => {
    if (session?.firmId) load();
  }, [session?.firmId, load]);

  const save = async (nextListed) => {
    setSaving(true);
    setError('');
    setSaved(false);
    try {
      const res = await updateFirmDirectory({
        firmId: session.firmId,
        listed: nextListed !== undefined ? nextListed : listed,
        ...profile,
      });
      setListed(!!res.directory?.listed);
      setSaved(true);
    } catch (e) {
      setError(e.message || 'Could not save the directory profile');
    } finally {
      setSaving(false);
    }
  };

  if (!isFirmAdmin) return null;
  if (loading)
    return <div style={{ fontSize: 13, color: 'var(--fg3)' }}>Loading directory profile…</div>;

  return (
    <div style={{ marginTop: 36 }}>
      <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--fg)', marginBottom: 4 }}>
        Directory listing
      </div>
      <p
        style={{
          fontSize: 12,
          color: 'var(--fg3)',
          margin: '0 0 16px',
          lineHeight: 1.6,
          maxWidth: 620,
        }}
      >
        Tryggr clients can browse audit firms that choose to be listed. Everything below is
        published to any signed-in client. Nothing about your clients or engagements is ever shown.
      </p>

      <div
        className="card"
        style={{
          padding: '14px 18px',
          marginBottom: 16,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
        }}
      >
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--fg)' }}>
            {listed ? 'Listed in the directory' : 'Not listed'}
          </div>
          <div style={{ fontSize: 12, color: 'var(--fg3)', marginTop: 2 }}>
            {listed
              ? 'Clients can find your firm and see the profile below.'
              : 'Your firm is invisible to clients until you list it.'}
          </div>
        </div>
        <button
          type="button"
          className={listed ? 'btn btn-sm' : 'btn btn-sm btn-primary'}
          disabled={saving}
          onClick={() => save(!listed)}
        >
          {listed ? 'Remove from directory' : 'List my firm'}
        </button>
      </div>

      {error && (
        <div
          style={{
            padding: '10px 14px',
            borderRadius: 8,
            marginBottom: 12,
            background: 'var(--danger-bg)',
            border: '1px solid var(--danger)',
            fontSize: 13,
            color: 'var(--danger)',
          }}
        >
          {error}
        </div>
      )}

      {FIELDS.map((f) => (
        <div key={f.key} style={{ marginBottom: 14, maxWidth: 620 }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: 'var(--fg3)',
              marginBottom: 5,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
            }}
          >
            {f.label}
          </div>
          {f.multiline ? (
            <textarea
              className="input"
              rows={4}
              maxLength={f.max}
              value={profile[f.key]}
              placeholder={f.placeholder}
              onChange={(e) => setProfile((p) => ({ ...p, [f.key]: e.target.value }))}
            />
          ) : (
            <input
              className="input"
              maxLength={f.max}
              value={profile[f.key]}
              placeholder={f.placeholder}
              onChange={(e) => setProfile((p) => ({ ...p, [f.key]: e.target.value }))}
            />
          )}
          {f.hint && (
            <div style={{ fontSize: 11, color: 'var(--fg3)', marginTop: 4 }}>{f.hint}</div>
          )}
        </div>
      ))}

      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button type="button" className="btn btn-primary" disabled={saving} onClick={() => save()}>
          {saving ? 'Saving…' : 'Save profile'}
        </button>
        {saved && <span style={{ fontSize: 12, color: 'var(--ok)' }}>Saved</span>}
      </div>
    </div>
  );
}
